import type {
  NodeComponentProps,
  NodeViewLiveProps,
  NodeViewStaticProps,
} from '../tiptap-helpers'
import type { ImageNodeAttributes } from './ImageNode'

import * as logger from '@/utils/logger'

import { NodeViewWrapper } from '@tiptap/react'
import { getResizedImageURL } from '@rundown-studio/utils'
import { modalActions } from '@/features/rundown/modals/modal.store'

export function ImageNodeViewLive({
  node,
  updateAttributes,
}: NodeViewLiveProps) {
  return (
    <NodeViewWrapper>
      <ImageNodeComponent node={node} updateImgAttrs={updateAttributes} />
    </NodeViewWrapper>
  )
}

export function ImageNodeViewStatic({ node }: NodeViewStaticProps) {
  return <ImageNodeComponent node={node} />
}

/**
 * Custom Tiptap node that renders uploaded images.
 *
 * Attempts to render an optimized image, falls back to original,
 * and opens a lightbox when clicked.
 *
 * @note Not exported, use the static or live wrappers depending on usecase.
 */
function ImageNodeComponent({
  node,
  updateImgAttrs,
}: NodeComponentProps & {
  updateImgAttrs?: (attrs: Partial<ImageNodeAttributes>) => void
}) {
  const attrs = node.attrs as ImageNodeAttributes

  const optimizedSrc = getResizedImageURL(attrs.src)

  const onLoad = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    // `updateAttributes` is only avaible in editor, not preview
    if (!updateImgAttrs) return

    // Only update attributes if they are missing or default.
    if (!attrs.width || !attrs.height) {
      updateImgAttrs({
        width: e.currentTarget.naturalWidth,
        height: e.currentTarget.naturalHeight,
      })
    }
  }

  const onError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const img = e.currentTarget
    logger.error('[ImageNodeView] Error: Failed to load image', img.srcset)

    img.srcset = '' // Clear srcset to break out of failure loop
    img.src = attrs.src // Force browser to fallback to original
  }

  const onClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    modalActions.open('CELL_IMAGENODE', {
      imageUrl: attrs.src,
    })
  }

  return (
    <img
      // Browser tries `srcSet` first before falling back to `src`
      srcSet={`${optimizedSrc} 1x`}
      src={attrs.src}
      // Only set w/h if they are not the default (`0`)
      width={attrs.width}
      height={attrs.height}
      alt=""
      loading="lazy"
      decoding="async"
      onLoad={onLoad}
      onError={onError}
      onClick={onClick}
      draggable={false} // Prevent native dragging (not draghandle)
      className="my-2 block bg-black/50 rounded-sm shadow-sm cursor-pointer hover:brightness-110"
      style={{
        aspectRatio:
          attrs.width && attrs.height
            ? `${attrs.width} / ${attrs.height}`
            : 'auto',
      }}
    />
  )
}
